import {
  BookOutlined,
  CalendarOutlined,
  LockOutlined,
  RedoOutlined,
  SaveOutlined,
  UnlockOutlined,
} from '@ant-design/icons'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Grid2 as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import Paper from 'components/@extended/Paper'
import dayjs from 'dayjs'
import type { FC } from 'react'
import type { Period } from 'types/periods'
import { formatDisplayDate, DATE_FORMAT } from 'types/periods'

interface RootPeriodCardProps {
  period: Period
  showDataVisibilityControls?: boolean
  showActions?: boolean
  showVisibilityDates?: boolean
  managerDataVisibilityDate?: dayjs.Dayjs | null
  employeeDataVisibilityDate?: dayjs.Dayjs | null
  isUpdating?: boolean
  isSubmitting?: boolean
  canUpdateVisibility?: boolean
  onManagerDateChange?: (date: dayjs.Dayjs | null) => void
  onEmployeeDateChange?: (date: dayjs.Dayjs | null) => void
  onSaveDates?: () => void
  onClosePeriod?: (period: Period) => void
  onReopenPeriod?: (period: Period) => void
  onLockPeriod?: (period: Period) => void
  onLockedPeriodInfo?: () => void
}

const RootPeriodCard: FC<RootPeriodCardProps> = ({
  period,
  showDataVisibilityControls = false,
  showActions = false,
  showVisibilityDates = true,
  managerDataVisibilityDate,
  employeeDataVisibilityDate,
  isUpdating = false,
  isSubmitting = false,
  canUpdateVisibility = true,
  onManagerDateChange,
  onEmployeeDateChange,
  onSaveDates,
  onClosePeriod,
  onReopenPeriod,
  onLockPeriod,
  onLockedPeriodInfo,
}) => {
  const theme = useTheme()

  const renderActions = () => {
    if (!showActions) return null

    const status = period.status?.toLowerCase()

    if (status === 'locked') {
      return (
        <Tooltip title="Period is Locked">
          <IconButton size="medium" sx={{ color: theme.palette.info.main }} onClick={onLockedPeriodInfo}>
            <LockOutlined />
          </IconButton>
        </Tooltip>
      )
    }

    if (!period.isClosed) {
      return (
        <Tooltip title="Close Period">
          <IconButton size="medium" sx={{ color: theme.palette.primary.main }} onClick={() => onClosePeriod?.(period)}>
            <BookOutlined />
          </IconButton>
        </Tooltip>
      )
    }

    return (
      <>
        <Tooltip title="Reopen Period">
          <IconButton
            size="medium"
            sx={{ color: theme.palette.success.main }}
            onClick={() => onReopenPeriod?.(period)}
            disabled={isSubmitting}
          >
            <RedoOutlined />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lock Period">
          <IconButton
            size="medium"
            sx={{ color: theme.palette.warning.main }}
            onClick={() => onLockPeriod?.(period)}
            disabled={isSubmitting}
          >
            <UnlockOutlined />
          </IconButton>
        </Tooltip>
      </>
    )
  }

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, height: 'fit-content' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <CalendarOutlined style={{ fontSize: '20px', color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              {period.label}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {!showActions && (
              <Typography variant="caption" color="text.secondary">
                Status: {period.isClosed ? 'Closed' : 'Open'}
              </Typography>
            )}
            {showActions && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Status: {period.isClosed ? 'Closed' : 'Open'}
                </Typography>
                <Box display="flex" gap={0.5}>
                  {renderActions()}
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Stack spacing={3}>
          <Grid container spacing={2} sx={{ px: 2 }}>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Start Date
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDisplayDate(period.startDate)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                End Date
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formatDisplayDate(period.endDate)}
              </Typography>
            </Grid>
            {/* Show existing data visibility dates if they exist but controls are not shown */}
            {!showDataVisibilityControls && showVisibilityDates && period.managerDataVisibilityDate && (
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Manager Progress Visibility
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatDisplayDate(period.managerDataVisibilityDate)}
                </Typography>
              </Grid>
            )}
            {!showDataVisibilityControls && showVisibilityDates && period.employeeDataVisibilityDate && (
              <Grid size={{ xs: 6 }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Employee Progress Visibility
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatDisplayDate(period.employeeDataVisibilityDate)}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Data Visibility Controls */}
          {showDataVisibilityControls && (
            <>
              <Box sx={{ pt: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Manager Progress Visibility Date"
                    value={managerDataVisibilityDate}
                    onChange={onManagerDateChange}
                    format={DATE_FORMAT}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        fullWidth: true,
                        InputLabelProps: {
                          shrink: true,
                          sx: {
                            '&.Mui-focused': {
                              color: (theme) => theme.palette.primary.main,
                            },
                          },
                        },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: (theme) => theme.shape.borderRadius * 5.25,
                            '& fieldset': {
                              borderColor: (theme) => theme.palette.primary.main,
                            },
                            '&:hover fieldset': {
                              borderColor: (theme) => theme.palette.primary.dark,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: (theme) => theme.palette.secondary.main,
                            },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Employee Progress Visibility Date"
                    value={employeeDataVisibilityDate}
                    onChange={onEmployeeDateChange}
                    format={DATE_FORMAT}
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        fullWidth: true,
                        InputLabelProps: {
                          shrink: true,
                          sx: {
                            '&.Mui-focused': {
                              color: (theme) => theme.palette.primary.main,
                            },
                          },
                        },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: (theme) => theme.shape.borderRadius * 5.25,
                            '& fieldset': {
                              borderColor: (theme) => theme.palette.primary.main,
                            },
                            '&:hover fieldset': {
                              borderColor: (theme) => theme.palette.primary.dark,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: (theme) => theme.palette.secondary.main,
                            },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box display="flex" justifyContent="flex-end">
                <Tooltip title={!canUpdateVisibility ? "You don't have permission to update visibility dates" : ''}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<SaveOutlined />}
                      onClick={onSaveDates}
                      disabled={isUpdating || !canUpdateVisibility}
                      sx={{ minWidth: 120 }}
                    >
                      {isUpdating ? <CircularProgress size={16} /> : 'Save Dates'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Paper>
  )
}

export default RootPeriodCard
